import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { DeleteIcon } from '../../assets/icons/DeleteIcon';
import { DownloadIcon } from '../../assets/icons/DownloadIcon';
import { EyeIcon } from '../../assets/icons/EyeIcon';
import { FileIcon } from '../../assets/icons/FileIcon';
import { ImageIcon } from '../../assets/icons/ImageIcon';
import IconButton from '../icon-button/IconButton';
import LoadingOverlay from '../loading-overlay/LoadingOverlay';
import { formatDateString } from '../../utils/dateHandling';
import { isDevice } from '../../utils/device-handling/deviceDetectionUtils';
import { isImageType } from '../../utils/stringHandling';
import { downloadFileFromBlob } from '../../utils/fileUtils';
import NotificationService from '../../services/NotificationService';
import { Link } from 'react-router-dom';
import ImageService from '../../services/ImageService';
import { DownloadInfo } from '../../models/DownloadInfo';

const TextColumn = (props: { title?: string; value: string; flex: string; error?: boolean }) => {
  const { title, value, flex, error } = props;
  return (
    <ColumnContainer error={error} flex={flex}>
      <ColumnHeader>{title}</ColumnHeader>
      <ColumnValue>{value}</ColumnValue>
    </ColumnContainer>
  );
};

interface Props {
  name: string;
  src?: string;
  date?: string;
  uploadedBy?: string;
  errorMessage?: string;
  type?: string;
  isImage?: boolean;
  handleRemove?: () => void;
  handleDownload?: (fileName: string) => DownloadInfo;
  handleThumbnail?: () => Promise<string>;
  onClick?: () => void;
}

const FilePreview = ({
  src,
  name,
  date,
  uploadedBy,
  errorMessage,
  type,
  isImage,
  handleRemove,
  handleDownload,
  handleThumbnail,
  onClick
}: Props) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [isFetchingImage, setIsFetchingImage] = useState(false);
  const [deeplink, setDeeplink] = useState<string>('');

  const isTablet = isDevice();

  // Callers pass these as inline arrow functions, so their identity changes on every
  // render of the parent. Keeping them in refs lets the effect below depend on what
  // actually identifies the image instead of re-fetching it on every render.
  const handleThumbnailRef = useRef(handleThumbnail);
  handleThumbnailRef.current = handleThumbnail;
  const handleDownloadRef = useRef(handleDownload);
  handleDownloadRef.current = handleDownload;

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    let createdUrl: string | undefined;

    if (handleThumbnailRef.current) {
      // Drop the previous thumbnail up front - its object URL is revoked on cleanup, so
      // leaving it in state would render a dead src until the new one resolves.
      setImgSrc(undefined);

      handleThumbnailRef
        .current()
        .then((url) => {
          // Torn down mid-flight: release the url we just created instead of leaking it.
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          createdUrl = url;
          setImgSrc(url);
        })
        .catch(() => !cancelled && NotificationService.error('Kunne ikke hente thumbnail.'));
    }

    if (handleDownloadRef.current && !isTablet) {
      const downloadInfo = handleDownloadRef.current(name);
      const link = ImageService.getDeeplink(downloadInfo);
      setDeeplink(link ?? '');
    }

    return () => {
      cancelled = true;
      // Object URLs live until revoked or the document unloads, so without this a
      // technician moving between tasks all day accumulates every thumbnail ever shown.
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [isImage, isTablet, name]);

  const renderPlaceholderIcon = () => (isImageType(type) ? <StyledImageIcon size="62px" /> : <StyledFileIcon size="62px" />);

  const downloadFile = () => {
    if (!handleDownload) return;
    setIsFetchingImage(true);

    const downloadInfo = handleDownload(name);
    ImageService.downloadImage(downloadInfo)
      .then((res) => {
        downloadFileFromBlob(name, res.data);
      })
      .catch(() => NotificationService.error('Kunne ikke hente filen. Prøv igen.'))
      .finally(() => setIsFetchingImage(false));
  };

  return (
    <Container onClick={onClick}>
      {imgSrc ? (
        <ImgContainer>
          <StyledImg src={imgSrc} alt={name} />
        </ImgContainer>
      ) : (
        renderPlaceholderIcon()
      )}

      <TextContainer>
        {errorMessage ? (
          <>
            <TextColumn error flex="4" title="Filnavn" value={name} />
            <TextColumn error flex="4" title="Error" value={errorMessage} />
            <DivFlex2 />
          </>
        ) : (
          <>
            <TextColumn flex="4" title="Filnavn" value={name} />
            {date && <TextColumn flex="4" title="Dato for upload" value={formatDateString(date)} />}
            {uploadedBy && <TextColumn flex="3" title="Uploadet af" value={uploadedBy} />}
          </>
        )}
      </TextContainer>

      {isFetchingImage ? (
        <LoadingOverlay />
      ) : (
        <ButtonContainer>
          {/* Desktop: Eye icon for images with deeplink */}
          {!isTablet && isImage && deeplink && (
            <StyledLink target="_blank" to={deeplink} onClick={(e) => e.stopPropagation()}>
              <EyeIcon size="20px" />
            </StyledLink>
          )}

          {/* Download / iPad behavior */}
          {isImage && isTablet ? (
            // On iPad for images, Eye icon calls onClick
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick();
              }}
            >
              <EyeIcon size="20px" />
            </IconButton>
          ) : (
            <>
              {handleDownload && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile();
                  }}
                >
                  <DownloadIcon size="20px" />
                </IconButton>
              )}
            </>
          )}

          {handleRemove && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
            >
              <DeleteIcon size="20px" />
            </IconButton>
          )}
        </ButtonContainer>
      )}
    </Container>
  );
};

// Styled components

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;
`;

const DivFlex2 = styled.div`
  flex: 2;
`;

const StyledImageIcon = styled(ImageIcon)`
  margin-right: ${(props) => props.theme.spacing(6)};
  fill: ${(props) => props.theme.palette.grey.black20};
`;

const StyledFileIcon = styled(FileIcon)`
  margin-right: ${(props) => props.theme.spacing(6)};
  fill: ${(props) => props.theme.palette.grey.black20};
`;

const ImgContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: ${(props) => props.theme.spacing(6)};
  width: ${(props) => props.theme.spacing(16)};
  height: ${(props) => props.theme.spacing(16)};
  overflow: hidden;
`;

const StyledImg = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

const TextContainer = styled.div`
  flex: 6;
  display: flex;
  column-gap: 48px;
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-left: auto;
  width: 168px;
  justify-content: flex-end;
`;

const ColumnContainer = styled.div<{ flex: string; error?: boolean }>`
  display: flex;
  flex-direction: column;
  flex: ${({ flex }) => flex};
  & span {
    color: ${(props) => (props.error ? 'red !important' : '')};
  }
`;

export const ColumnHeader = styled.span`
  color: ${(props) => props.theme.palette.grey.black60};
  font-weight: 600;
  font-size: 13px;
`;

export const ColumnValue = styled.span`
  color: ${(props) => props.theme.palette.main.black.primary};
  word-break: break-word;
`;

export const StyledLink = styled(Link)`
  padding: ${(props) => props.theme.spacing(3)};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

export default FilePreview;
